import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { wishlistService } from '../services/wishlist';
import { logout } from './authSlice';
// Async Thunks
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (payload, { rejectWithValue }) => {
    const { userId, rowsPerPage,page } = payload;
    const response = await wishlistService.fetchWishlist(userId,rowsPerPage,page);
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const addToWishlist = createAsyncThunk(
  'wishlist/addToWishlist',
  async (data, { rejectWithValue }) => {
    const response = await wishlistService.addToWishlist(data);
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const updateWishlistItem = createAsyncThunk(
  'wishlist/updateWishlistItem',
  async ({ itemId, data }, { rejectWithValue }) => {
    const response = await wishlistService.updateWishlistItem(itemId, data);
    if (response.status >= 400) return rejectWithValue(response.data);
    return response.data;
  }
);

export const removeFromWishlist = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (itemId, { rejectWithValue }) => {
    const response = await wishlistService.removeFromWishlist(itemId);
    if (response.status >= 400) return rejectWithValue(response.data);
    return itemId;
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState: {
    wishItems: {
      data: [],
      meta: {},
    },
    loading: false,
    error: null,
    lastUpdated: null,
  },
  reducers: {
    resetWishlistError: (state) => {
      state.error = null;
    },
    sortWishlistByPrice: (state) => {
      state.wishItems.sort((a, b) => a.latest_price - b.latest_price);
    },
    sortWishlistByDate: (state) => {
      state.wishItems.sort((a, b) => new Date(b.last_reminder_date) - new Date(a.last_reminder_date));
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.wishItems.data = action.payload?.data;
        state.wishItems.meta = action.payload?.meta;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add to Wishlist
      .addCase(addToWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure state.items is an array and push the new item
        if (Array.isArray(state.wishItems?.data)) {
          state.wishItems.data.push(action.payload?.data); // Add the item
          state.wishItems.meta.total = state.wishItems.meta.total + 1; // Update the total count
        } else {
          // If state.items is not an array, initialize it
          state.wishItems.data = [action.payload?.data];
          state.wishItems.meta = { total: 1 };
        }
        state.lastUpdated = new Date().toISOString();
      })
      
      .addCase(addToWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update Wishlist Item
      .addCase(updateWishlistItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWishlistItem.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.wishItems?.data?.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.wishItems[index] = action.payload;
        }
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateWishlistItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove from Wishlist
      .addCase(removeFromWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.loading = false;
        // console.log(action.payload);
        state.wishItems.data = state?.wishItems?.data?.filter(item => item?.id !== action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(logout.fulfilled, (state) => {
        state.wishItems = {
          data: [],
          meta: {},
        };
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetWishlistError, sortWishlistByPrice, sortWishlistByDate } = wishlistSlice.actions;
export default wishlistSlice.reducer;